import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { useMemo } from 'react'

import { positionBasedCanScroll } from './position-based-can-scroll'

import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from 'embla-carousel'

// `watchDrag` also accepts a per-pointer-down predicate, so the gate reads Embla's measured track
// instead of a card count — and gates wheel too, since WheelGesturesPlugin drags via synthetic
// bubbling mouse events. Same signal `positionBasedCanScroll` uses, so the two agree by
// construction. Module scope: Embla compares option functions by source string.
const canDragTrack = (embla: EmblaCarouselType) => embla.scrollSnapList().length > 1

interface FreeDragCarouselArgs {
  slidesToScroll: number
  /** `false` = can never scroll, so opt out of drag + wheel (`dragFree` rubber-bands a zero-range
   * track). `true` doesn't assert overflow — `canDragTrack` still has the last word. */
  hasOverflow?: boolean
  /** Pass a module constant — a fresh object each render re-memoizes. */
  breakpoints?: EmblaOptionsType['breakpoints']
}

/** Free-drag config for the DS swimlanes. `positionBasedCanScroll` is required alongside `dragFree`:
 * Embla's own canScroll checks compare snap indexes and strand the arrows mid free-scroll. */
export const useFreeDragCarousel = ({ slidesToScroll, hasOverflow = true, breakpoints }: FreeDragCarouselArgs) => {
  const opts = useMemo<EmblaOptionsType>(
    () => ({
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: true,
      slidesToScroll,
      watchDrag: hasOverflow && canDragTrack,
      ...(breakpoints && { breakpoints }),
    }),
    [slidesToScroll, hasOverflow, breakpoints],
  )

  const plugins = useMemo<EmblaPluginType[]>(
    () => (hasOverflow ? [WheelGesturesPlugin(), positionBasedCanScroll()] : [positionBasedCanScroll()]),
    [hasOverflow],
  )

  return { opts, plugins }
}
